import { useMemo, useState } from "react";
import { Card, StatusIndicator, type StatusTone } from "./ui";
import { LabeledField, FindingList, NO_DISCORD_CHANGES, HUMAN_APPROVAL_REQUIRED } from "./onboarding";
import {
  MODERATION_ACTIONS,
  ENFORCEMENT_ACTIONS,
  AUTOMOD_RULE_TYPES,
  AUTOMOD_ACTIONS,
  LOG_EVENT_CATEGORIES,
  type AutomodAction,
  type AutomodModule,
  type AutomodPreviewResult,
  type AutomodPreviewSample,
  type AutomodRule,
  type AutomodRuleType,
  type EscalationRecommendation,
  type LogEventCategory,
  type LoggingModule,
  type ModerationActionRecord,
  type ModerationActionType,
  type ModerationCase,
  type ModerationCaseStatus,
  type TakeActionInput,
  type WarningSummary,
} from "../lib/api";
import { formatDateTime } from "../lib/format";

/**
 * Presentational + pure building blocks for the Phase 6B moderation / automod /
 * logging surfaces. Everything here is deliberately data-fetch-free so it can be
 * unit tested with mock data, and everything is honest about what it does.
 *
 * SAFETY LANGUAGE (used across these surfaces):
 *  - Moderation actions run through the approval-bound, LOCKED pipeline. The
 *    dashboard never directly bans / kicks / times out a member — it records an
 *    action request that stays locked (dry-run) until the platform is unlocked.
 *  - A reason is REQUIRED for every enforcement action.
 *  - Automod does not scan messages unless the privileged Message Content intent
 *    is enabled and an operator turns it on.
 *  - Drafts / dry-runs / previews: "No Discord changes have been made."
 */
export const LOCKED_PIPELINE_NOTE =
  "Moderation actions run through the approval-bound, locked pipeline. This dashboard never directly bans, kicks, or times out a member — the platform is in dry-run / locked mode, so an action is only recorded and queued. No live Discord effect happens until the platform is deliberately unlocked.";
export const REASON_REQUIRED_NOTE = "A reason is required for every enforcement action (warn, timeout, kick, ban).";
export const MESSAGE_CONTENT_INTENT_NOTE =
  "Automod does not scan messages unless the privileged Message Content intent is enabled for the bot and an operator turns it on. Without it, these rules describe intent only — nothing is scanned.";
export const NO_LIVE_EFFECT = "No live Discord change has been made — the action was recorded through the locked pipeline.";

// ── Moderation action metadata ───────────────────────────────────────────────
export const ACTION_META: Record<ModerationActionType, { label: string; blurb: string; enforcement: boolean; needsDuration: boolean }> = {
  WARN: { label: "Warn", blurb: "Record a warning. Drives the escalation recommendation.", enforcement: true, needsDuration: false },
  TIMEOUT: { label: "Timeout", blurb: "Temporarily mute a member for a set duration.", enforcement: true, needsDuration: true },
  UNTIMEOUT: { label: "Remove timeout", blurb: "Lift an active timeout. A reversal — reason optional.", enforcement: false, needsDuration: false },
  KICK: { label: "Kick", blurb: "Remove a member; they may rejoin with a new invite.", enforcement: true, needsDuration: false },
  BAN: { label: "Ban", blurb: "Remove a member and block re-entry.", enforcement: true, needsDuration: false },
  UNBAN: { label: "Unban", blurb: "Lift a ban. A reversal — reason optional.", enforcement: false, needsDuration: false },
  NOTE: { label: "Note", blurb: "Record an internal note. Not an enforcement action.", enforcement: false, needsDuration: false },
};

export function needsReason(action: ModerationActionType): boolean {
  return ENFORCEMENT_ACTIONS.includes(action);
}

/** Pure validation for a take-action draft. Returns human-readable blockers. */
export function takeActionErrors(input: TakeActionInput): string[] {
  const errors: string[] = [];
  if (!input.subjectDiscordUserId.trim()) errors.push("A target Discord user id is required.");
  if (needsReason(input.action) && !(input.reason ?? "").trim()) errors.push("A reason is required for enforcement actions.");
  if (input.action === "TIMEOUT" && (!input.durationSeconds || input.durationSeconds <= 0)) {
    errors.push("A timeout needs a positive duration (in seconds).");
  }
  return errors;
}

// ── Case status mapping ──────────────────────────────────────────────────────
export function caseStatusTone(status: ModerationCaseStatus): StatusTone {
  switch (status) {
    case "ACTIONED":
      return "warn";
    case "OPEN":
      return "neutral";
    case "APPEALED":
      return "warn";
    case "OVERTURNED":
      return "danger";
    case "EXPIRED":
    case "CLOSED":
      return "neutral";
    default:
      return "neutral";
  }
}

// Case lifecycle transitions (mirrors @odin/moderation case-state, presentational only).
const CASE_TRANSITIONS: Record<ModerationCaseStatus, ModerationCaseStatus[]> = {
  OPEN: ["ACTIONED", "CLOSED"],
  ACTIONED: ["APPEALED", "EXPIRED", "CLOSED"],
  APPEALED: ["OVERTURNED", "CLOSED"],
  OVERTURNED: ["CLOSED"],
  EXPIRED: ["CLOSED"],
  CLOSED: [],
};

export function canTransition(from: ModerationCaseStatus, to: ModerationCaseStatus): boolean {
  return CASE_TRANSITIONS[from]?.includes(to) ?? false;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatDateTime(d);
}

// ── Take-action form (the guarded, locked path) ──────────────────────────────
export function TakeActionForm({
  onSubmit,
  busy = false,
  result,
  disabled = false,
}: {
  onSubmit?: (input: TakeActionInput) => void;
  busy?: boolean;
  result?: string | null;
  disabled?: boolean;
}): JSX.Element {
  const [action, setAction] = useState<ModerationActionType>("WARN");
  const [subject, setSubject] = useState("");
  const [reason, setReason] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(600);

  const meta = ACTION_META[action];
  const input: TakeActionInput = {
    action,
    subjectDiscordUserId: subject,
    reason: reason.trim() ? reason.trim() : null,
    durationSeconds: meta.needsDuration ? durationSeconds : null,
  };
  const errors = takeActionErrors(input);
  const canSubmit = errors.length === 0 && !busy && !disabled && Boolean(onSubmit);

  return (
    <Card title="Take a moderation action">
      <div className="dryrun-banner" role="note">
        <strong>Dry-run / locked mode.</strong>
        <span>{LOCKED_PIPELINE_NOTE}</span>
      </div>

      <div className="card-grid">
        <LabeledField label="Action">
          <select className="field__input" value={action} onChange={(e) => setAction(e.target.value as ModerationActionType)} aria-label="Moderation action">
            {MODERATION_ACTIONS.map((a) => (
              <option key={a} value={a}>
                {ACTION_META[a].label}
              </option>
            ))}
          </select>
        </LabeledField>
        <LabeledField label="Target Discord user id" hint="Only the target's Discord user id is sent. All actor/target facts are re-resolved server-side.">
          <input className="field__input" value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Target Discord user id" placeholder="e.g. 123456789012345678" />
        </LabeledField>
        {meta.needsDuration ? (
          <LabeledField label="Timeout duration (seconds)" hint="Required for a timeout.">
            <input className="field__input" type="number" min={1} value={durationSeconds} onChange={(e) => setDurationSeconds(Number(e.target.value))} aria-label="Timeout duration seconds" />
          </LabeledField>
        ) : null}
      </div>

      <LabeledField label={needsReason(action) ? "Reason (required)" : "Reason (optional)"} hint={meta.blurb}>
        <textarea className="field__input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason" placeholder={needsReason(action) ? "Required — why this enforcement action is being taken" : "Optional context"} />
      </LabeledField>

      {errors.length > 0 ? (
        <ul className="ob-findings" aria-label="Action blockers">
          {errors.map((e, i) => (
            <li key={i} className="ob-finding ob-finding--danger">
              <span className="sev sev--danger">BLOCKER</span> {e}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="row" style={{ gap: "0.5rem", marginTop: "0.75rem", alignItems: "center" }}>
        <button type="button" className="btn btn--primary" disabled={!canSubmit} onClick={() => onSubmit?.(input)}>
          {busy ? "Recording…" : `Record ${meta.label} request`}
        </button>
        <span className="muted">{NO_LIVE_EFFECT}</span>
      </div>

      {result ? (
        <div className="world-panel world-panel--proposed" role="status" style={{ marginTop: "1rem" }}>
          <p>{result}</p>
        </div>
      ) : null}

      <p className="muted" style={{ marginTop: "0.5rem" }}>
        {REASON_REQUIRED_NOTE} There is deliberately no button here that bans or mutates a member directly.
      </p>
    </Card>
  );
}

// ── Case list ────────────────────────────────────────────────────────────────
export function CaseList({
  cases,
  selectedId,
  onSelect,
}: {
  cases: ModerationCase[];
  selectedId?: string | null;
  onSelect?: (caseId: string) => void;
}): JSX.Element {
  if (cases.length === 0) {
    return (
      <Card title="Moderation cases">
        <p className="muted">No moderation cases recorded for this server yet.</p>
      </Card>
    );
  }
  return (
    <Card title={`Moderation cases (${cases.length})`} description="Real records from ODIN's case store. Selecting a case shows its action history.">
      <div className="op-list">
        {cases.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`list-row${selectedId === c.id ? "" : " list-row--muted"}`}
            style={{ cursor: "pointer", textAlign: "left" }}
            aria-pressed={selectedId === c.id}
            onClick={() => onSelect?.(c.id)}
          >
            <StatusIndicator tone={caseStatusTone(c.status)} label={c.status} />
            <span>
              <strong>#{c.caseNumber ?? "—"}</strong> {c.actionType}
            </span>
            <span className="muted">
              subject {c.subjectDiscordUserId} · {c.reason ? c.reason.slice(0, 60) : "no reason"} · {fmtDate(c.createdAt)}
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

// ── Case detail + lifecycle ──────────────────────────────────────────────────
export function CaseDetail({
  detail,
  onAppeal,
  onOverturn,
  onClose,
  busy = false,
  message,
}: {
  detail: { case: ModerationCase; actions: ModerationActionRecord[] };
  onAppeal?: (caseId: string) => void;
  onOverturn?: (caseId: string) => void;
  onClose?: (caseId: string) => void;
  busy?: boolean;
  message?: string | null;
}): JSX.Element {
  const c = detail.case;
  return (
    <Card title={`Case #${c.caseNumber ?? "—"} · ${c.actionType}`} description="Action history is an append-only audit trail — historical actions are never deleted.">
      <div className="kv-grid">
        <div className="kv">
          <span className="kv__k">Status</span>
          <span className="kv__v">
            <StatusIndicator tone={caseStatusTone(c.status)} label={c.status} />
          </span>
        </div>
        <div className="kv">
          <span className="kv__k">Subject</span>
          <span className="kv__v kv__v--mono">{c.subjectDiscordUserId}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Reason</span>
          <span className="kv__v">{c.reason ?? "— none recorded —"}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Active</span>
          <span className="kv__v">{c.active ? "Yes" : "No"}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Opened</span>
          <span className="kv__v">{fmtDate(c.createdAt)}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Expires</span>
          <span className="kv__v">{fmtDate(c.expiresAt)}</span>
        </div>
      </div>

      <h4 className="section-title">Action history ({detail.actions.length})</h4>
      {detail.actions.length === 0 ? (
        <p className="muted">No individual action records for this case.</p>
      ) : (
        <div className="op-list">
          {detail.actions.map((a) => (
            <div key={a.id} className="list-row list-row--muted" style={{ cursor: "default" }}>
              <span className="badge">{a.actionType}</span>
              <span>{a.result}</span>
              <span className="muted">
                {a.failureCode ? `${a.failureCode} · ` : ""}
                {a.reason ? `${a.reason.slice(0, 60)} · ` : ""}
                {fmtDate(a.createdAt)}
              </span>
            </div>
          ))}
        </div>
      )}

      {message ? (
        <div className="world-panel world-panel--current" role="status" style={{ marginTop: "0.75rem" }}>
          <p>{message}</p>
        </div>
      ) : null}

      <div className="row" style={{ gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
        <button type="button" className="btn btn--secondary" disabled={busy || !onAppeal || !canTransition(c.status, "APPEALED")} onClick={() => onAppeal?.(c.id)}>
          Appeal
        </button>
        <button type="button" className="btn btn--secondary" disabled={busy || !onOverturn || !canTransition(c.status, "OVERTURNED")} onClick={() => onOverturn?.(c.id)}>
          Overturn
        </button>
        <button type="button" className="btn btn--secondary" disabled={busy || !onClose || !canTransition(c.status, "CLOSED")} onClick={() => onClose?.(c.id)}>
          Close
        </button>
      </div>
      <p className="muted" style={{ marginTop: "0.5rem" }}>
        Overturning a case deactivates its warnings and requires an OWNER/ADMIN. These lifecycle changes update ODIN records — {NO_LIVE_EFFECT}
      </p>
    </Card>
  );
}

// ── Member warnings + escalation recommendation ──────────────────────────────
export function escalationText(rec: EscalationRecommendation | null): string {
  if (!rec) return "No escalation recommended at the current warning count.";
  const dur = rec.recommend === "TIMEOUT" && rec.timeoutSeconds ? ` (${rec.timeoutSeconds}s)` : "";
  return `At ${rec.atWarnings}+ active warnings the configured ladder recommends: ${rec.recommend}${dur}.`;
}

export function MemberWarningsView({
  summary,
  onExpire,
  busy = false,
}: {
  summary: WarningSummary;
  onExpire?: (warningId: string) => void;
  busy?: boolean;
}): JSX.Element {
  return (
    <Card title="Member warnings" description="Active warnings and the advisory escalation recommendation. The recommendation is advisory — a human still authorizes any action through the guarded path.">
      <div className="kv-grid">
        <div className="kv">
          <span className="kv__k">Subject</span>
          <span className="kv__v kv__v--mono">{summary.subjectDiscordUserId}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Active warnings</span>
          <span className="kv__v">{summary.activeWarnings}</span>
        </div>
      </div>

      <div className="world-panel world-panel--desired" role="status" style={{ marginTop: "0.5rem" }}>
        <StatusIndicator tone={summary.escalation ? "warn" : "ok"} label={summary.escalation ? `Recommended: ${summary.escalation.recommend}` : "No escalation recommended"} />
        <p className="muted">{escalationText(summary.escalation)}</p>
      </div>

      <h4 className="section-title">Active warnings</h4>
      {summary.warnings.length === 0 ? (
        <p className="muted">No active warnings for this member.</p>
      ) : (
        <div className="op-list">
          {summary.warnings.map((w) => (
            <div key={w.id} className="list-row list-row--muted" style={{ cursor: "default" }}>
              <span className="badge">{w.active ? "active" : "expired"}</span>
              <span>{w.reason.slice(0, 80)}</span>
              <span className="muted">{fmtDate(w.createdAt)}</span>
              <button type="button" className="btn btn--ghost" disabled={busy || !onExpire || !w.active} onClick={() => onExpire?.(w.id)} aria-label={`Expire warning ${w.id}`}>
                Expire
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="muted" style={{ marginTop: "0.5rem" }}>
        Expiring a warning is a record change only. Escalating still requires an authorized action through the locked, guarded pipeline.
      </p>
    </Card>
  );
}

// ── Automod rule builder (structured — never raw JSON) ────────────────────────
export interface AutomodDraftFinding {
  severity: "blocker" | "warning" | "info";
  message: string;
}

export function emptyAutomodRule(id = ""): AutomodRule {
  return { id, type: "keyword", enabled: true, action: "flag", terms: [], exemptRoleKeys: [] };
}

export function emptyAutomodModule(): AutomodModule {
  return { enabled: true, schemaVersion: "1", rules: [] };
}

/** Structural validation of an automod draft — the subset checkable in-browser. */
export function validateAutomodDraft(module: AutomodModule): AutomodDraftFinding[] {
  const findings: AutomodDraftFinding[] = [];
  if (module.rules.length === 0) findings.push({ severity: "warning", message: "No rules defined yet — add at least one rule." });
  const seen = new Set<string>();
  for (const rule of module.rules) {
    const label = rule.id.trim() || "(unnamed rule)";
    if (!rule.id.trim()) findings.push({ severity: "blocker", message: "Every rule needs a stable id." });
    else if (seen.has(rule.id)) findings.push({ severity: "blocker", message: `Duplicate rule id "${rule.id}".` });
    if (rule.id) seen.add(rule.id);

    if ((rule.type === "keyword" || rule.type === "link-domain") && rule.terms.length === 0) {
      findings.push({ severity: "blocker", message: `Rule "${label}" (${rule.type}) needs at least one term.` });
    }
    if (rule.type === "mass-mention" && (rule.maxMentions === undefined || rule.maxMentions < 1)) {
      findings.push({ severity: "blocker", message: `Rule "${label}" (mass-mention) needs a maxMentions value.` });
    }
    if (rule.type === "repeated-text" && (rule.maxRepeats === undefined || rule.maxRepeats < 2)) {
      findings.push({ severity: "blocker", message: `Rule "${label}" (repeated-text) needs a maxRepeats value of 2 or more.` });
    }
    if (rule.action === "timeout" && (rule.timeoutSeconds === undefined || rule.timeoutSeconds < 1)) {
      findings.push({ severity: "warning", message: `Rule "${label}" times out but has no timeoutSeconds — the server default would apply.` });
    }
  }
  return findings;
}

const RULE_TYPE_LABEL: Record<AutomodRuleType, string> = {
  keyword: "Keyword match",
  "invite-link": "Discord invite link",
  "mass-mention": "Mass mention",
  "repeated-text": "Repeated text",
  "link-domain": "Link domain",
};

const ACTION_LABEL: Record<AutomodAction, string> = {
  flag: "Flag (record only)",
  delete: "Delete message",
  warn: "Warn author",
  timeout: "Timeout author",
};

function csv(list: string[]): string {
  return list.join(", ");
}
function parseCsv(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function AutomodBuilder({
  initial,
  onValidateRemote,
  onGenerateDraft,
  onPreview,
}: {
  initial?: AutomodModule;
  onValidateRemote?: (module: AutomodModule) => void;
  onGenerateDraft?: (module: AutomodModule) => void;
  onPreview?: (module: AutomodModule, sample: AutomodPreviewSample) => Promise<AutomodPreviewResult>;
}): JSX.Element {
  const [module, setModule] = useState<AutomodModule>(initial ?? emptyAutomodModule());
  const [sample, setSample] = useState<AutomodPreviewSample>({ content: "", mentionCount: 0, authorRoleKeys: [], recentIdenticalCount: 0 });
  const [preview, setPreview] = useState<AutomodPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const findings = validateAutomodDraft(module);
  const blockers = findings.filter((f) => f.severity === "blocker");
  const valid = blockers.length === 0 && module.rules.length > 0;

  const patchRule = (i: number, p: Partial<AutomodRule>) =>
    setModule((m) => ({ ...m, rules: m.rules.map((r, idx) => (idx === i ? { ...r, ...p } : r)) }));
  const addRule = () => setModule((m) => ({ ...m, rules: [...m.rules, emptyAutomodRule(`rule-${m.rules.length + 1}`)] }));
  const removeRule = (i: number) => setModule((m) => ({ ...m, rules: m.rules.filter((_, idx) => idx !== i) }));

  const runPreview = async () => {
    if (!onPreview) return;
    setBusy(true);
    setPreviewError(null);
    try {
      setPreview(await onPreview(module, sample));
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : "Preview failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>{NO_DISCORD_CHANGES}</strong>
        <span>{MESSAGE_CONTENT_INTENT_NOTE}</span>
      </div>

      <Card title={`Rules (${module.rules.length})`} description="Build rules structurally — never raw component JSON. Exempt roles are entered as blueprint resource keys.">
        {module.rules.map((rule, i) => (
          <div key={i} className="world-panel world-panel--proposed" style={{ marginBottom: "0.75rem" }}>
            <div className="card-grid">
              <LabeledField label="Rule id">
                <input className="field__input" value={rule.id} onChange={(e) => patchRule(i, { id: e.target.value })} aria-label={`Rule ${i + 1} id`} />
              </LabeledField>
              <LabeledField label="Type">
                <select className="field__input" value={rule.type} onChange={(e) => patchRule(i, { type: e.target.value as AutomodRuleType })} aria-label={`Rule ${i + 1} type`}>
                  {AUTOMOD_RULE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {RULE_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </LabeledField>
              <LabeledField label="Action">
                <select className="field__input" value={rule.action} onChange={(e) => patchRule(i, { action: e.target.value as AutomodAction })} aria-label={`Rule ${i + 1} action`}>
                  {AUTOMOD_ACTIONS.map((a) => (
                    <option key={a} value={a}>
                      {ACTION_LABEL[a]}
                    </option>
                  ))}
                </select>
              </LabeledField>
            </div>

            {rule.type === "keyword" || rule.type === "link-domain" ? (
              <LabeledField label={rule.type === "keyword" ? "Blocked terms (comma-separated)" : "Blocked domains (comma-separated)"}>
                <input className="field__input" value={csv(rule.terms)} onChange={(e) => patchRule(i, { terms: parseCsv(e.target.value) })} aria-label={`Rule ${i + 1} terms`} />
              </LabeledField>
            ) : null}
            {rule.type === "mass-mention" ? (
              <LabeledField label="Max mentions" hint="Messages with more mentions than this fire the rule.">
                <input className="field__input" type="number" min={1} max={50} value={rule.maxMentions ?? ""} onChange={(e) => patchRule(i, { maxMentions: e.target.value ? Number(e.target.value) : undefined })} aria-label={`Rule ${i + 1} max mentions`} />
              </LabeledField>
            ) : null}
            {rule.type === "repeated-text" ? (
              <LabeledField label="Max repeats" hint="Firing when the same message is repeated at least this many times.">
                <input className="field__input" type="number" min={2} max={20} value={rule.maxRepeats ?? ""} onChange={(e) => patchRule(i, { maxRepeats: e.target.value ? Number(e.target.value) : undefined })} aria-label={`Rule ${i + 1} max repeats`} />
              </LabeledField>
            ) : null}
            {rule.action === "timeout" ? (
              <LabeledField label="Timeout seconds (optional)">
                <input className="field__input" type="number" min={1} value={rule.timeoutSeconds ?? ""} onChange={(e) => patchRule(i, { timeoutSeconds: e.target.value ? Number(e.target.value) : undefined })} aria-label={`Rule ${i + 1} timeout seconds`} />
              </LabeledField>
            ) : null}

            <LabeledField label="Exempt role keys (comma-separated)" hint="Members with these roles are never actioned by this rule.">
              <input className="field__input" value={csv(rule.exemptRoleKeys)} onChange={(e) => patchRule(i, { exemptRoleKeys: parseCsv(e.target.value) })} aria-label={`Rule ${i + 1} exempt role keys`} />
            </LabeledField>
            <label className="row" style={{ gap: "0.5rem", alignItems: "center", marginTop: "0.5rem" }}>
              <input type="checkbox" checked={rule.enabled} onChange={(e) => patchRule(i, { enabled: e.target.checked })} aria-label={`Rule ${i + 1} enabled`} />
              <span>Enabled</span>
              <button type="button" className="btn btn--ghost" onClick={() => removeRule(i)} aria-label={`Remove rule ${i + 1}`} style={{ marginLeft: "auto" }}>
                Remove
              </button>
            </label>
          </div>
        ))}
        <button type="button" className="btn btn--secondary" onClick={addRule}>
          Add rule
        </button>
      </Card>

      <Card title="Validation">
        <StatusIndicator tone={valid ? "ok" : "danger"} label={valid ? "Structurally valid" : `${blockers.length} blocker(s)`} />
        <FindingList findings={findings} emptyLabel="No structural problems found." />
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          Structural checks run here in the browser. Full validation (unresolved exempt-role references, duplicate ids) runs server-side when you generate a draft. {NO_DISCORD_CHANGES}
        </p>
      </Card>

      <Card title="Test against a sample message" description="Runs the rules over a sample you type. Read-only — it never scans live Discord content.">
        <LabeledField label="Sample message content">
          <textarea className="field__input" rows={2} value={sample.content} onChange={(e) => setSample((s) => ({ ...s, content: e.target.value }))} aria-label="Sample message content" />
        </LabeledField>
        <div className="card-grid">
          <LabeledField label="Mention count">
            <input className="field__input" type="number" min={0} value={sample.mentionCount ?? 0} onChange={(e) => setSample((s) => ({ ...s, mentionCount: Number(e.target.value) }))} aria-label="Sample mention count" />
          </LabeledField>
          <LabeledField label="Recent identical count">
            <input className="field__input" type="number" min={0} value={sample.recentIdenticalCount ?? 0} onChange={(e) => setSample((s) => ({ ...s, recentIdenticalCount: Number(e.target.value) }))} aria-label="Sample recent identical count" />
          </LabeledField>
          <LabeledField label="Author role keys (comma-separated)">
            <input className="field__input" value={csv(sample.authorRoleKeys ?? [])} onChange={(e) => setSample((s) => ({ ...s, authorRoleKeys: parseCsv(e.target.value) }))} aria-label="Sample author role keys" />
          </LabeledField>
        </div>
        <button type="button" className="btn btn--primary" disabled={busy || !onPreview || !sample.content.trim()} onClick={runPreview} style={{ marginTop: "0.5rem" }}>
          {busy ? "Testing…" : "Test against sample"}
        </button>
        {previewError ? <p className="sev sev--danger" role="alert" style={{ marginTop: "0.5rem" }}>{previewError}</p> : null}
        {preview ? (
          <div className="world-panel world-panel--current" role="status" style={{ marginTop: "0.75rem" }}>
            <StatusIndicator tone={preview.matched ? "warn" : "ok"} label={preview.matched ? `${preview.violations.length} rule(s) fired` : "No rules fired"} />
            <p className="muted">Evaluated {preview.ruleCount} rule(s).</p>
            {preview.violations.length > 0 ? (
              <ul className="ob-findings" aria-label="Fired rules">
                {preview.violations.map((v, i) => (
                  <li key={i} className="ob-finding ob-finding--warn">
                    <span className="badge">{v.type}</span> rule <code>{v.ruleId}</code> → {v.action}: {v.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </Card>

      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
        <button type="button" className="btn btn--secondary" disabled={!valid || !onGenerateDraft} onClick={() => onGenerateDraft?.(module)}>
          Generate dry-run draft &amp; validate
        </button>
        <button type="button" className="btn btn--secondary" disabled={!valid || !onValidateRemote} onClick={() => onValidateRemote?.(module)}>
          Validate against server
        </button>
      </div>
      <p className="muted" style={{ marginTop: "0.5rem" }}>
        {NO_DISCORD_CHANGES} {HUMAN_APPROVAL_REQUIRED} Activation only links a draft to a deployment that already cleared Phase 4 review.
      </p>
    </div>
  );
}

// ── Logging config ───────────────────────────────────────────────────────────
const CATEGORY_LABEL: Record<LogEventCategory, string> = {
  moderation: "Moderation actions",
  "member-join": "Member join",
  "member-leave": "Member leave",
  "role-change": "Role changes",
  "channel-change": "Channel changes",
  onboarding: "Onboarding",
  automod: "Automod",
  deployment: "Deployments",
};

export function emptyLoggingModule(): LoggingModule {
  return { enabled: true, schemaVersion: "1", logChannelKey: undefined, categories: [], redactContent: true };
}

export function LoggingConfigForm({
  initial,
  onValidateRemote,
  onActivate,
  message,
  busy = false,
}: {
  initial?: LoggingModule;
  onValidateRemote?: (module: LoggingModule) => void;
  onActivate?: (module: LoggingModule) => void;
  message?: string | null;
  busy?: boolean;
}): JSX.Element {
  const [module, setModule] = useState<LoggingModule>(initial ?? emptyLoggingModule());
  const patch = (p: Partial<LoggingModule>) => setModule((m) => ({ ...m, ...p }));
  const toggleCategory = (cat: LogEventCategory) =>
    setModule((m) => ({
      ...m,
      categories: m.categories.includes(cat) ? m.categories.filter((c) => c !== cat) : [...m.categories, cat],
    }));

  const findings = useMemo<AutomodDraftFinding[]>(() => {
    const f: AutomodDraftFinding[] = [];
    if (!module.logChannelKey?.trim()) f.push({ severity: "blocker", message: "Select a log channel (resource key)." });
    if (module.categories.length === 0) f.push({ severity: "warning", message: "No event categories selected — nothing would be logged." });
    return f;
  }, [module]);
  const valid = !findings.some((f) => f.severity === "blocker");

  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>{NO_DISCORD_CHANGES}</strong>
        <span>Choose where operational events are logged and what is captured. Activation routes through the Phase 4 pipeline.</span>
      </div>

      <Card title="Log destination">
        <LabeledField label="Log channel (resource key)" hint="From your blueprint, e.g. channel.audit-log. No Discord channel id is entered here.">
          <input className="field__input" value={module.logChannelKey ?? ""} onChange={(e) => patch({ logChannelKey: e.target.value || undefined })} aria-label="Log channel resource key" placeholder="channel.audit-log" />
        </LabeledField>
      </Card>

      <Card title="Event categories" description="Select which categories of events ODIN records to the log channel.">
        <div className="card-grid">
          {LOG_EVENT_CATEGORIES.map((cat) => (
            <label key={cat} className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
              <input type="checkbox" checked={module.categories.includes(cat)} onChange={() => toggleCategory(cat)} aria-label={CATEGORY_LABEL[cat]} />
              <span>{CATEGORY_LABEL[cat]}</span>
            </label>
          ))}
        </div>
      </Card>

      <Card title="Content redaction">
        <label className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <input type="checkbox" checked={module.redactContent} onChange={(e) => patch({ redactContent: e.target.checked })} aria-label="Redact message content in logs" />
          <span>Redact message content in logs</span>
        </label>
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          When enabled, logged events omit raw message content. Log entries are always built through a secret-free builder — credentials can never appear in a log.
        </p>
      </Card>

      <Card title="Validation">
        <StatusIndicator tone={valid ? "ok" : "danger"} label={valid ? "Ready to validate" : "Needs a log channel"} />
        <FindingList findings={findings} emptyLabel="No problems found." />
      </Card>

      {message ? (
        <div className="world-panel world-panel--current" role="status">
          <p>{message}</p>
        </div>
      ) : null}

      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
        <button type="button" className="btn btn--secondary" disabled={busy || !valid || !onValidateRemote} onClick={() => onValidateRemote?.(module)}>
          Save draft &amp; validate
        </button>
        <button type="button" className="btn btn--secondary" disabled={busy || !valid || !onActivate} onClick={() => onActivate?.(module)}>
          Activate through pipeline
        </button>
      </div>
      <p className="muted" style={{ marginTop: "0.5rem" }}>
        {NO_DISCORD_CHANGES} {HUMAN_APPROVAL_REQUIRED} Activation only links to a deployment that already cleared Phase 4 approval — there is no direct-activate button that bypasses it.
      </p>
    </div>
  );
}
